import { initializeCopyFields } from "../../Shared/CopyField/copy-field";
import { updateProgressBar } from "../../Shared/ProgressBar/progress-bar";
import { getRequiredElement } from "../../Shared/Utilities/attributes";

enum Direction {
  Previous = "previous",
  Next = "next"
}

let timeout: NodeJS.Timeout;

/**
 * Adds and removes CSS classes to control the display of an element.
 */
const setElementDisplay = (
  element: HTMLElement,
  addClass: string,
  removeClass: string = "hidden"
) => {
  element.classList.remove(removeClass);
  element.classList.add(addClass);
};

/**
 * Resets quiz navigation state by hiding navigation buttons.
 */
const resetQuizShowState = (quizContainer: HTMLElement) => {
  quizContainer.dataset.showPrevious = "false";
  quizContainer.dataset.showNext = "false";
  quizContainer.dataset.showSubmit = "false";
};

/**
 * Initializes quiz functionality, including navigation and progress tracking.
 */
const initQuiz = (quiz: HTMLElement): void => {
  let activeStep = 0;

  const quizContainer = getRequiredElement<HTMLElement>(
    quiz,
    "[data-quiz-steps]",
    "No quiz steps form found."
  );

  const pageUrlInput = getRequiredElement<HTMLInputElement>(
    quizContainer,
    "#pageUrlInput",
    "PageUrlInput not found."
  );

  pageUrlInput.value = window.location.href;

  /**
   * Handle showing the results returned by htmx,
   * hide the quiz step
   * and trigger the call to get the CopyField component
   */
  quizContainer.addEventListener("htmx:afterOnLoad", (event: Event) => {
    resetQuizShowState(quizContainer);

    const shareContainer = getRequiredElement<HTMLElement>(
      quiz,
      "[data-quiz-share-container]",
      "Share container not found."
    );

    setElementDisplay(shareContainer, "flex");

    const resultsContainer = getRequiredElement<HTMLElement>(
      quizContainer,
      "[data-quiz-results-container]",
      "Results container not found."
    );

    setElementDisplay(resultsContainer, "flex");

    const progressBarContainer = getRequiredElement<HTMLElement>(
      quiz,
      "[data-progress-bar-container]",
      "Progress bar container not found."
    );

    setElementDisplay(progressBarContainer, "hidden", "flex");

    const stepsList = getRequiredElement<HTMLElement>(
      quizContainer,
      "ol",
      "Steps list not found"
    );

    setElementDisplay(stepsList, "hidden", "grid");

    steps.forEach(step => {
      step.dataset.stepActive = "false";
    });

    const showAnswersButton = getRequiredElement<HTMLElement>(
      resultsContainer,
      "[data-step-show-answers-button]",
      "Show answers button not found."
    );

    showAnswersButton.addEventListener("click", () => {
      resetQuizShowState(quizContainer);

      setElementDisplay(resultsContainer, "hidden", "flex");

      setElementDisplay(stepsList, "grid");

      const progressBarContainer = getRequiredElement<HTMLElement>(
        quiz,
        "[data-progress-bar-container]",
        "Progress bar container not found."
      );

      setElementDisplay(progressBarContainer, "flex");

      const showAnswers = quiz.querySelectorAll<HTMLElement>("[data-show-answers]");

      showAnswers.forEach(elem => {
        elem.dataset.showAnswers = "true";
      });

      const answerOptions = quiz.querySelectorAll<HTMLInputElement>(
        "[data-step-option] input[type='radio']"
      );

      answerOptions.forEach(option => {
        option.disabled = true;
      });

      activeStep = 0;
      steps[activeStep].dataset.stepActive = "true";
      setElementDisplay(steps[activeStep], "opacity-1", "opacity-0");

      updateProgressBar(activeStep + 1);
      updateNavigationButtons();
    });

    initializeCopyFields();
    animateHeight();
  });

  /**
   * Updates navigation buttons based on the current step and user input.
   */
  const updateNavigationButtons = (): void => {
    resetQuizShowState(quizContainer);

    if (activeStep > 0) {
      quizContainer.dataset.showPrevious = "true";
    }

    const selectedOption = steps[activeStep].querySelector<HTMLInputElement>(
      "[data-step-option] input:checked"
    );

    const isLastStep = activeStep === steps.length - 1;
    if (selectedOption) {
      quizContainer.dataset.showNext = isLastStep ? "false" : "true";
      quizContainer.dataset.showSubmit = isLastStep ? "true" : "false";
    }
  };

  /**
   * Handles navigation between quiz steps.
   */
  const navigateStep = (direction: Direction) => {
    const currentStep = steps[activeStep];
    setElementDisplay(currentStep, "opacity-0", "opacity-1");
    currentStep.dataset.stepActive = "false";

    activeStep += direction === Direction.Previous ? -1 : 1;
    const nextStep = steps[activeStep];
    nextStep.dataset.stepActive = "true";

    setElementDisplay(nextStep, "opacity-1", "opacity-0");

    updateProgressBar(activeStep + 1);
    updateNavigationButtons();
  };

  const intro = getRequiredElement<HTMLElement>(
    quiz,
    "[data-quiz-intro]",
    "Intro missing."
  );

  const startButton = getRequiredElement<HTMLElement>(
    intro,
    "[data-quiz-start]",
    "Start button not found."
  );

  startButton.addEventListener("click", () => {
    intro.style.display = "none";

    steps[activeStep].dataset.stepActive = "true";
    setElementDisplay(steps[activeStep], "opacity-1", "opacity-0");

    const progressBarContainer = getRequiredElement<HTMLElement>(
      quiz,
      "[data-progress-bar-container]",
      "Progress bar container not found."
    );

    setElementDisplay(progressBarContainer, "flex");

    updateProgressBar(activeStep + 1);
  });

  const summaryButton = getRequiredElement<HTMLElement>(
    quiz,
    "[data-step-summary-button]",
    "Summary button not found."
  );

  summaryButton.addEventListener("click", () => {
    resetQuizShowState(quizContainer);

    const resultsContainer = getRequiredElement<HTMLElement>(
      quizContainer,
      "[data-quiz-results-container]",
      "Results container not found."
    );

    setElementDisplay(resultsContainer, "flex");

    const progressBarContainer = getRequiredElement<HTMLElement>(
      quiz,
      "[data-progress-bar-container]",
      "Progress bar container not found."
    );

    setElementDisplay(progressBarContainer, "hidden", "flex");

    const stepsList = getRequiredElement<HTMLElement>(
      quizContainer,
      "ol",
      "Steps list not found"
    );

    setElementDisplay(stepsList, "hidden", "grid");

    steps.forEach(step => {
      step.dataset.stepActive = "false";
    });

    setElementDisplay(resultsContainer, "opacity-1", "opacity-0");
  });

  const steps = quiz.querySelectorAll<HTMLElement>("[data-quiz-step]");

  const nextButton = getRequiredElement<HTMLElement>(
    quiz,
    "[data-step-next-button]",
    "Next button not found."
  );

  nextButton.addEventListener("click", () => navigateStep(Direction.Next));

  const previousButton = getRequiredElement<HTMLElement>(
    quiz,
    "[data-step-previous-button]",
    "Previous button not found."
  );

  previousButton.addEventListener("click", () => navigateStep(Direction.Previous));

  const answerOptions = quiz.querySelectorAll<HTMLInputElement>(
    "[data-step-option] input[type='radio']"
  );

  answerOptions.forEach(option => {
    option.addEventListener("change", updateNavigationButtons);
  });

  /**
   * Dynamically set the height to allow for animation
   */
  const animateHeight = () => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      const startHeight = parseInt(getComputedStyle(quizContainer).height);
      quizContainer.style.height = "auto";

      requestAnimationFrame(() => {
        const endHeight = parseInt(getComputedStyle(quizContainer).height);

        quizContainer.style.height = `${startHeight}px`;

        requestAnimationFrame(() => {
          quizContainer.style.height = `${endHeight}px`;
        });
      });
    }, 50);
  };

  quizContainer.addEventListener("click", animateHeight);
  window.addEventListener("load", animateHeight);
};

document.querySelectorAll<HTMLElement>("[id^='quiz_']").forEach(quiz => initQuiz(quiz));
